import * as THREE from "three";
import { Engine } from "../../game/engine";
import { AudioBed } from "../../game/audio";
import { Projectiles, DodgeEvent } from "./projectiles";

/**
 * Hailstorm v3 — depth rebuild.
 *
 * The arena and projectile schedule are unchanged (server-replayed for
 * anti-cheat). On top of that flat dodge loop we now add:
 *
 *   1. **Three active abilities** in a pre-run loadout. The player picks
 *      two of {Phase, Anchor, Foresight}. Each is anti-cheat compatible
 *      because it only affects whether the player ends the run, not what
 *      projectiles do or where the ship was (the trace is honest).
 *        - Phase  ("dash" kind)  — 0.7 s of i-frames, 5 s cooldown.
 *        - Anchor ("mine" kind)  — drop a stationary 3-s i-frame field
 *          at the current position, 8 s cooldown.
 *        - Foresight ("slow" kind) — reveals projectile trajectories for
 *          2 s; 2 charges per run. Pure information, no defense.
 *
 *   2. **Wave schedule** — every WAVE_LEN_S the wave cycles through a
 *      named set (Sprinkle → Stream → Hailfront → Crossfire → Storm →
 *      The Eye → repeat). Bosses (Hailfront, The Eye) spawn a stationary
 *      rim emitter as cosmetic identity; the projectile schedule itself
 *      stays unchanged. A name banner slides in at each wave change.
 *
 *   3. **Per-wave audio + visual identity** — fog tint shifts, drone
 *      bed intensity ramps, environment pulses.
 *
 * Anti-cheat properties preserved: projectile schedule deterministic
 * from seed; trace at 10 Hz; dodge claims server-replayed; ability uses
 * validated for cooldown/charge by server. The wave-clear and boss-
 * survive bonuses are server-derived from survival_s — client cannot
 * forge them.
 */

const ARENA_HALF = 20;
const SHIP_RADIUS = 0.45;
const PB_KEY = "aether.hailstorm_pb";
const LOADOUT_KEY = "aether.hailstorm_loadout";
const WAVE_LEN_S = 30;
const APPROACH_GATE = 4.5;

const HIT_RADIUS = 0.9;
const SLOW_CHARGES_PER_RUN = 2;
const PHASE_COOLDOWN_S = 5;
const PHASE_DURATION_S = 0.7;
const ANCHOR_COOLDOWN_S = 8;
const ANCHOR_DURATION_S = 3;
const ANCHOR_RADIUS = 2.5;
const FORESIGHT_DURATION_S = 2;

export type AbilityKind = "dash" | "mine" | "slow";
export interface AbilityUse { t: number; kind: AbilityKind }
const ALL_ABILITIES: AbilityKind[] = ["dash", "mine", "slow"];
const ABILITY_LABEL: Record<AbilityKind, string> = { dash: "Phase", mine: "Anchor", slow: "Foresight" };

interface BiomePalette { floor: number; rim: number; ship: number; shipEmissive: number; fog: number; }
const PALETTES: Record<string, BiomePalette> = {
  sprinkle:  { floor: 0x0a0207, rim: 0xff8a32, ship: 0x00e0ff, shipEmissive: 0x0080ff, fog: 0x150508 },
  stream:    { floor: 0x021214, rim: 0x46ffe0, ship: 0xffd54a, shipEmissive: 0xff8800, fog: 0x021c1c },
  hailfront: { floor: 0x140304, rim: 0xff3060, ship: 0xffffff, shipEmissive: 0xff5b3a, fog: 0x1a0408 },
  crossfire: { floor: 0x0a0518, rim: 0x9a72ff, ship: 0xff3df0, shipEmissive: 0xff00aa, fog: 0x150728 },
  storm:     { floor: 0x100805, rim: 0xff5b3a, ship: 0xffd54a, shipEmissive: 0xff2010, fog: 0x180a05 },
  "the-eye": { floor: 0x05030a, rim: 0xff2010, ship: 0x88ccff, shipEmissive: 0xff5b3a, fog: 0x100308 },
};

const WAVE_CYCLE: { slug: keyof typeof PALETTES; name: string; boss: boolean }[] = [
  { slug: "sprinkle",  name: "Sprinkle",  boss: false },
  { slug: "stream",    name: "Stream",    boss: false },
  { slug: "hailfront", name: "Hailfront", boss: true },
  { slug: "crossfire", name: "Crossfire", boss: false },
  { slug: "storm",     name: "Storm",     boss: false },
  { slug: "the-eye",   name: "The Eye",   boss: true },
];

export interface HailstormCallbacks {
  onHud: (state: {
    survivalS: number;
    dodges: number;
    waveName: string;
    waveBoss: boolean;
    /** Seconds remaining on each ability (0 = ready). For slow this is
     *  always 0 — slowCharges carries the meaningful state. */
    cooldowns: { dash: number; mine: number; slow: number };
    slowCharges: number;
    /** Active ability flag for HUD highlight: "phase" | "anchor" | "foresight" | null. */
    activeFx: "phase" | "anchor" | "foresight" | null;
  }) => void;
  /** Fired once when the wave changes — UI surfaces the name banner. */
  onWaveStart: (name: string, boss: boolean) => void;
  onGameOver: (result: {
    survivalS: number;
    score: number;
    dodges: number;
    isNewBest: boolean;
    dodgeClaims: { idx: number; distance: number; t?: number }[];
    trace: number[];
    abilitiesUsed: AbilityUse[];
    loadout: AbilityKind[];
    wavesCleared: number;
    bossesSurvived: number;
    bossKills: { wave_idx: number }[];
  }) => void;
}

export class Hailstorm {
  private engine: Engine;
  private audio = new AudioBed();
  private projectiles: Projectiles;
  private dodgeClaims: { idx: number; distance: number; t?: number }[] = [];
  private trace: number[] = [];
  private lastSampleT = 0;
  private lastSampleX = 0;
  private lastSampleY = 0;
  private static readonly SAMPLE_INTERVAL_S = 0.1;
  // If more than this elapses between recorder ticks (tab throttled or rAF
  // pause), backfill linearly at SAMPLE_INTERVAL_S so the trace stays at
  // the expected 10 Hz cadence instead of leaving a single fat sample that
  // trips the validator's irregular-interval / supersonic-speed checks.
  private static readonly SAMPLE_INTERVAL_MAX_S = 0.25;
  private ship: THREE.Group;
  private shipMat: THREE.MeshStandardMaterial;
  private shipPos = new THREE.Vector2();
  private pointerPos = new THREE.Vector2();
  private pointerActive = false;
  private startedAt = 0;
  private gameOver = false;
  // v5(a) — seed retained so playReplay() can re-derive the projectile
  // schedule. The schedule depends on (seed + ship-trajectory-at-spawn-
  // time), so re-running with the same seed AND feeding the same ship
  // positions reproduces the run frame-for-frame.
  private seed: number;
  // In-memory replay viewer state. When replayMode is true, frame()
  // ignores input and drives ship position from the recorded trace.
  // No server endpoint, no persistence — the player replays their own
  // just-finished run before navigating away.
  private replayMode = false;
  private replayTrace: number[] = [];
  private replayDodgeIdx = new Set<number>();
  private replayT = 0;
  private replayEndT = 0;
  private replaySpeed = 1;
  private replayDoneCb: (() => void) | null = null;
  private cb: HailstormCallbacks;
  private canvas: HTMLCanvasElement;
  private floorMat: THREE.MeshStandardMaterial;
  private rimMat: THREE.MeshBasicMaterial;
  private gridLines: THREE.LineSegments;
  private gridMat: THREE.LineBasicMaterial;
  private currentBiome: keyof typeof PALETTES = "sprinkle";
  private targetBiome: keyof typeof PALETTES = "sprinkle";
  private biomeBlend = 0;
  private dodgeSpike = 0;
  private speedLines: THREE.Points;
  private speedLinesMat: THREE.PointsMaterial;

  // v3 ability state
  private loadout: AbilityKind[] = ["dash", "mine"];
  private abilitiesUsed: AbilityUse[] = [];
  private cooldowns: Record<AbilityKind, number> = { dash: 0, mine: 0, slow: 0 };
  private slowCharges = SLOW_CHARGES_PER_RUN;
  private phaseUntil = 0;
  private foresightUntil = 0;
  private anchors: { x: number; y: number; expiresAt: number; mesh: THREE.Mesh; haloMat: THREE.MeshBasicMaterial }[] = [];

  // v3 wave state
  private currentWaveIdx = -1;
  private bossMesh: THREE.Mesh | null = null;
  private bossMat: THREE.MeshStandardMaterial | null = null;
  private bossHp = 0;
  private bossMaxHp = 5;
  private bossWaveIdx = -1;
  private bossDamageFlash = 0;
  private bossHpRing: THREE.Mesh | null = null;
  private bossHpRingMat: THREE.MeshBasicMaterial | null = null;
  private bossKills: { wave_idx: number }[] = [];

  // Boss visual flicker.
  private bossPulse = 0;
  // Radial trauma pulse on boss-kill. 0..1; ramped to 1.0 on the kill
  // frame and decayed back to 0 over ~0.6s. Driven through engine.setBoost
  // so the kill reads at higher event-density via the shared FOV + motion-
  // blur + chromatic + god-rays passes. Silent when motion effects are off.
  private bossKillPulse = 0;

  private onPointerDown = (e: PointerEvent) => { this.pointerActive = true; this.updatePointer(e); };
  private onPointerMove = (e: PointerEvent) => { if (this.pointerActive) this.updatePointer(e); };
  private onPointerUp = () => { this.pointerActive = false; };
  private onKeyDown = (e: KeyboardEvent) => {
    if (e.code === "Digit1" || e.code === "KeyQ") this.tryUseAbility(0);
    else if (e.code === "Digit2" || e.code === "KeyE") this.tryUseAbility(1);
  };

  constructor(canvas: HTMLCanvasElement, cb: HailstormCallbacks, seed = Math.floor(Math.random() * 0x7fffffff)) {
    this.canvas = canvas;
    this.cb = cb;
    this.seed = seed;
    this.engine = new Engine(canvas, { onFrame: (dt, t) => this.frame(dt, t) });

    this.engine.camera.position.set(0, -12, 22);
    this.engine.camera.lookAt(0, 4, 0);
    this.engine.camera.fov = 56;
    this.engine.camera.updateProjectionMatrix();
    this.engine.scene.fog = new THREE.Fog(PALETTES.sprinkle.fog, 28, 70);

    const ambient = new THREE.AmbientLight(0x5070a0, 0.5);
    this.engine.scene.add(ambient);
    const key = new THREE.DirectionalLight(0xfff0d0, 0.8);
    key.position.set(8, 4, 14);
    this.engine.scene.add(key);

    const floorGeo = new THREE.PlaneGeometry(ARENA_HALF * 4, ARENA_HALF * 4, 1, 1);
    this.floorMat = new THREE.MeshStandardMaterial({ color: PALETTES.sprinkle.floor, roughness: 0.95, metalness: 0.1 });
    const floor = new THREE.Mesh(floorGeo, this.floorMat);
    floor.position.z = -0.5;
    this.engine.scene.add(floor);

    const gridPts: number[] = [];
    for (let i = -ARENA_HALF * 2; i <= ARENA_HALF * 2; i += 2) {
      gridPts.push(i, -ARENA_HALF * 2, -0.49, i, ARENA_HALF * 2, -0.49);
      gridPts.push(-ARENA_HALF * 2, i, -0.49, ARENA_HALF * 2, i, -0.49);
    }
    const gridGeo = new THREE.BufferGeometry();
    gridGeo.setAttribute("position", new THREE.Float32BufferAttribute(gridPts, 3));
    this.gridMat = new THREE.LineBasicMaterial({ color: PALETTES.sprinkle.rim, transparent: true, opacity: 0.16, blending: THREE.AdditiveBlending });
    this.gridLines = new THREE.LineSegments(gridGeo, this.gridMat);
    this.engine.scene.add(this.gridLines);

    const rimGeo = new THREE.RingGeometry(ARENA_HALF * 1.42, ARENA_HALF * 1.46, 96);
    this.rimMat = new THREE.MeshBasicMaterial({ color: PALETTES.sprinkle.rim, transparent: true, opacity: 0.6, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    const rim = new THREE.Mesh(rimGeo, this.rimMat);
    rim.position.z = -0.48;
    rim.rotation.z = Math.PI / 4;
    this.engine.scene.add(rim);

    this.shipMat = new THREE.MeshStandardMaterial({ color: PALETTES.sprinkle.ship, emissive: PALETTES.sprinkle.shipEmissive, emissiveIntensity: 1.2, roughness: 0.3, metalness: 0.6 });
    const shipBodyGeo = new THREE.ConeGeometry(SHIP_RADIUS, SHIP_RADIUS * 1.4, 16);
    const shipBody = new THREE.Mesh(shipBodyGeo, this.shipMat);
    shipBody.rotation.x = Math.PI / 2;
    const haloGeo = new THREE.RingGeometry(SHIP_RADIUS + 0.18, SHIP_RADIUS + 0.42, 32);
    const haloMat = new THREE.MeshBasicMaterial({ color: PALETTES.sprinkle.ship, transparent: true, opacity: 0.4, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    const halo = new THREE.Mesh(haloGeo, haloMat);
    halo.position.z = -0.05;
    this.ship = new THREE.Group();
    this.ship.add(shipBody);
    this.ship.add(halo);
    this.engine.scene.add(this.ship);

    this.projectiles = new Projectiles(seed);
    this.engine.scene.add(this.projectiles.group);

    const lineCount = 60;
    const linePts = new Float32Array(lineCount * 3);
    for (let i = 0; i < lineCount; i++) {
      const a = (i / lineCount) * Math.PI * 2;
      const r = 1.2 + Math.random() * 0.8;
      linePts[i * 3 + 0] = Math.cos(a) * r;
      linePts[i * 3 + 1] = Math.sin(a) * r;
      linePts[i * 3 + 2] = 0;
    }
    const lineGeo = new THREE.BufferGeometry();
    lineGeo.setAttribute("position", new THREE.BufferAttribute(linePts, 3));
    this.speedLinesMat = new THREE.PointsMaterial({ color: 0xffffff, size: 0.28, transparent: true, opacity: 0, blending: THREE.AdditiveBlending, depthWrite: false });
    this.speedLines = new THREE.Points(lineGeo, this.speedLinesMat);
    this.engine.scene.add(this.speedLines);

    canvas.addEventListener("pointerdown", this.onPointerDown);
    canvas.addEventListener("pointermove", this.onPointerMove);
    window.addEventListener("pointerup", this.onPointerUp);
    window.addEventListener("keydown", this.onKeyDown);
  }

  setMuted(muted: boolean) { this.audio.setMuted(muted); }
  setVolume(v: number) { this.audio.setVolume(v); }
  armAudio() { this.audio.arm(); }

  setLoadout(loadout: AbilityKind[]) {
    // Pick (up to) 2 abilities. Default if invalid.
    const filtered = loadout.filter((k) => ALL_ABILITIES.includes(k));
    this.loadout = filtered.length > 0 ? filtered.slice(0, 2) : ["dash", "mine"];
  }

  start() {
    this.startedAt = this.engine.clock.elapsedTime;
    this.gameOver = false;
    this.shipPos.set(0, 0);
    this.dodgeClaims = [];
    this.trace = [];
    this.lastSampleT = -Infinity;
    this.lastSampleX = 0;
    this.lastSampleY = 0;
    this.currentBiome = "sprinkle";
    this.targetBiome = "sprinkle";
    this.biomeBlend = 0;
    this.dodgeSpike = 0;
    this.currentWaveIdx = -1;
    this.abilitiesUsed = [];
    this.bossKills = [];
    this.bossKillPulse = 0;
    this.cooldowns = { dash: 0, mine: 0, slow: 0 };
    this.slowCharges = SLOW_CHARGES_PER_RUN;
    this.phaseUntil = 0;
    this.foresightUntil = 0;
    for (const a of this.anchors) {
      this.engine.scene.remove(a.mesh);
      a.mesh.geometry.dispose();
      a.haloMat.dispose();
    }
    this.anchors = [];
    this.clearBoss();
    this.applyBiome(PALETTES.sprinkle);
    this.audio.setDroneIntensity(0.45);
    this.engine.start();
  }

  private updatePointer(e: PointerEvent) {
    const rect = this.canvas.getBoundingClientRect();
    const nx = ((e.clientX - rect.left) / rect.width) * 2 - 1;
    const ny = -(((e.clientY - rect.top) / rect.height) * 2 - 1);
    const ndc = new THREE.Vector3(nx, ny, 0.5).unproject(this.engine.camera);
    const dir = ndc.sub(this.engine.camera.position).normalize();
    const dist = -this.engine.camera.position.z / dir.z;
    const wx = this.engine.camera.position.x + dir.x * dist;
    const wy = this.engine.camera.position.y + dir.y * dist;
    this.pointerPos.set(wx, wy);
  }

  private applyBiome(p: BiomePalette) {
    this.floorMat.color.setHex(p.floor);
    this.rimMat.color.setHex(p.rim);
    this.gridMat.color.setHex(p.rim);
    this.shipMat.color.setHex(p.ship);
    this.shipMat.emissive.setHex(p.shipEmissive);
    if (this.engine.scene.fog instanceof THREE.Fog) {
      this.engine.scene.fog.color.setHex(p.fog);
    }
  }

  private blendBiomes(a: BiomePalette, b: BiomePalette, t: number) {
    this.floorMat.color.copy(new THREE.Color(a.floor).lerp(new THREE.Color(b.floor), t));
    this.rimMat.color.copy(new THREE.Color(a.rim).lerp(new THREE.Color(b.rim), t));
    this.gridMat.color.copy(new THREE.Color(a.rim).lerp(new THREE.Color(b.rim), t));
    this.shipMat.color.copy(new THREE.Color(a.ship).lerp(new THREE.Color(b.ship), t));
    this.shipMat.emissive.copy(new THREE.Color(a.shipEmissive).lerp(new THREE.Color(b.shipEmissive), t));
    if (this.engine.scene.fog instanceof THREE.Fog) {
      this.engine.scene.fog.color.copy(new THREE.Color(a.fog).lerp(new THREE.Color(b.fog), t));
    }
  }

  private spawnBoss(slug: keyof typeof PALETTES, waveIdx: number) {
    this.clearBoss();
    const palette = PALETTES[slug];
    const bodyGeo = new THREE.SphereGeometry(2.2, 24, 18);
    this.bossMat = new THREE.MeshStandardMaterial({ color: palette.rim, emissive: palette.rim, emissiveIntensity: 2.0, roughness: 0.3, metalness: 0.55 });
    this.bossMesh = new THREE.Mesh(bodyGeo, this.bossMat);
    this.bossMesh.position.set(ARENA_HALF * 0.75, ARENA_HALF * 0.75, 0);
    this.engine.scene.add(this.bossMesh);
    // HP ring — 5 segments around the boss, drains as HP drops.
    const hpRingGeo = new THREE.RingGeometry(2.6, 2.9, 64, 1, 0, Math.PI * 2);
    this.bossHpRingMat = new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.85, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
    this.bossHpRing = new THREE.Mesh(hpRingGeo, this.bossHpRingMat);
    this.bossHpRing.position.copy(this.bossMesh.position);
    this.engine.scene.add(this.bossHpRing);
    this.bossHp = this.bossMaxHp;
    this.bossWaveIdx = waveIdx;
    this.bossDamageFlash = 0;
    this.audio.chime(-12, 0.6);
    this.audio.chime(-19, -0.6);
  }

  /** Deal one HP of damage to the active boss. Called when an Anchor is
   *  placed within damage range. Returns true if the boss died this hit. */
  private damageBoss(): boolean {
    if (!this.bossMesh || this.bossHp <= 0) return false;
    this.bossHp -= 1;
    this.bossDamageFlash = 1;
    this.audio.chime(7, -0.3);
    if (this.bossHp <= 0) {
      // Boss death — record kill, play cinematic chime, fade boss.
      this.bossKills.push({ wave_idx: this.bossWaveIdx });
      this.audio.chime(12, 0);
      this.audio.chime(19, 0.3);
      this.audio.chime(24, -0.3);
      // Cinematic flash via dodge-spike reuse for the speed-lines effect.
      this.dodgeSpike = 1.0;
      // Kick the radial trauma pulse (FOV punch + motion blur via engine).
      this.bossKillPulse = 1.0;
      return true;
    }
    return false;
  }

  private clearBoss() {
    if (this.bossMesh) {
      this.engine.scene.remove(this.bossMesh);
      this.bossMesh.geometry.dispose();
      this.bossMat?.dispose();
      this.bossMesh = null;
      this.bossMat = null;
    }
    if (this.bossHpRing) {
      this.engine.scene.remove(this.bossHpRing);
      this.bossHpRing.geometry.dispose();
      this.bossHpRingMat?.dispose();
      this.bossHpRing = null;
      this.bossHpRingMat = null;
    }
    this.bossHp = 0;
    this.bossWaveIdx = -1;
  }

  /** Use the ability in slot 0 or 1 of the loadout. Public so the
   *  bootstrap can wire on-screen tap buttons in addition to Q/E keys. */
  tryUseAbility(slotIdx: number) {
    if (this.gameOver) return;
    const kind = this.loadout[slotIdx];
    if (!kind) return;
    if (kind === "slow") {
      if (this.slowCharges <= 0) return;
      this.slowCharges--;
    } else {
      if (this.cooldowns[kind] > 0.01) return;
      this.cooldowns[kind] = kind === "dash" ? PHASE_COOLDOWN_S : ANCHOR_COOLDOWN_S;
    }
    const t = this.engine.clock.elapsedTime - this.startedAt;
    this.abilitiesUsed.push({ t: Math.max(0, t), kind });

    if (kind === "dash") {
      this.phaseUntil = this.engine.clock.elapsedTime + PHASE_DURATION_S;
      this.audio.chime(7, 0);
    } else if (kind === "mine") {
      // Drop anchor at current ship position.
      const haloGeo = new THREE.RingGeometry(ANCHOR_RADIUS - 0.1, ANCHOR_RADIUS, 48);
      const haloMat = new THREE.MeshBasicMaterial({ color: 0x46ffe0, transparent: true, opacity: 0.6, blending: THREE.AdditiveBlending, side: THREE.DoubleSide });
      const halo = new THREE.Mesh(haloGeo, haloMat);
      halo.position.set(this.shipPos.x, this.shipPos.y, -0.1);
      this.engine.scene.add(halo);
      this.anchors.push({
        x: this.shipPos.x,
        y: this.shipPos.y,
        expiresAt: this.engine.clock.elapsedTime + ANCHOR_DURATION_S,
        mesh: halo,
        haloMat,
      });
      this.audio.chime(4, 0);
      // v4: if the anchor lands inside ANCHOR_RADIUS of an active boss,
      // it deals 1 HP. Both Hailfront and The Eye take damage this way.
      if (this.bossMesh && this.bossHp > 0) {
        const bx = this.bossMesh.position.x;
        const by = this.bossMesh.position.y;
        const d = Math.hypot(bx - this.shipPos.x, by - this.shipPos.y);
        if (d <= ANCHOR_RADIUS + 1.6) {
          this.damageBoss();
        }
      }
    } else { // slow → foresight reveal
      this.foresightUntil = this.engine.clock.elapsedTime + FORESIGHT_DURATION_S;
      this.audio.chime(12, 0);
      this.audio.chime(7, 0);
    }
  }

  private isProtected(): boolean {
    const now = this.engine.clock.elapsedTime;
    if (now < this.phaseUntil) return true;
    for (const a of this.anchors) {
      const d = Math.hypot(a.x - this.shipPos.x, a.y - this.shipPos.y);
      if (d <= ANCHOR_RADIUS && now < a.expiresAt) return true;
    }
    return false;
  }

  private frame(dt: number, t: number) {
    if (this.replayMode) { this.replayFrame(dt); return; }
    if (this.gameOver) return;

    if (this.pointerActive) {
      const dx = this.pointerPos.x - this.shipPos.x;
      const dy = this.pointerPos.y - this.shipPos.y;
      const d = Math.hypot(dx, dy);
      const maxSpeed = 24;
      const move = Math.min(d, maxSpeed * dt);
      if (d > 0.001) {
        this.shipPos.x += (dx / d) * move;
        this.shipPos.y += (dy / d) * move;
      }
    }
    this.shipPos.x = Math.max(-ARENA_HALF, Math.min(ARENA_HALF, this.shipPos.x));
    this.shipPos.y = Math.max(-ARENA_HALF, Math.min(ARENA_HALF, this.shipPos.y));
    this.ship.position.set(this.shipPos.x, this.shipPos.y, 0);
    const moveDx = this.pointerActive ? (this.pointerPos.x - this.shipPos.x) : 0;
    this.ship.rotation.z = -Math.atan2(moveDx, 8) * 0.3;

    const runT = t - this.startedAt;

    // Wave transitions.
    const waveIdx = Math.floor(runT / WAVE_LEN_S);
    if (waveIdx !== this.currentWaveIdx && waveIdx >= 0) {
      this.currentWaveIdx = waveIdx;
      const waveSlug = WAVE_CYCLE[waveIdx % WAVE_CYCLE.length].slug;
      const waveName = WAVE_CYCLE[waveIdx % WAVE_CYCLE.length].name;
      const isBoss = WAVE_CYCLE[waveIdx % WAVE_CYCLE.length].boss;
      this.currentBiome = this.targetBiome;
      this.targetBiome = waveSlug;
      this.biomeBlend = 0;
      // Boss visual.
      if (isBoss) this.spawnBoss(waveSlug, waveIdx);
      else this.clearBoss();
      this.cb.onWaveStart(waveName, isBoss);
      // Drone intensity per wave.
      const droneByWave = isBoss ? 0.85 : (waveIdx === 0 ? 0.4 : 0.55 + (waveIdx * 0.02));
      this.audio.setDroneIntensity(Math.min(0.95, droneByWave));
    }

    // Biome blend animation.
    if (this.currentBiome !== this.targetBiome) {
      this.biomeBlend = Math.min(1, this.biomeBlend + dt * 0.5);
      this.blendBiomes(PALETTES[this.currentBiome], PALETTES[this.targetBiome], this.biomeBlend);
      if (this.biomeBlend >= 1) this.currentBiome = this.targetBiome;
    }

    // Boss visual breathing + HP ring + damage flash.
    if (this.bossMesh && this.bossMat) {
      this.bossPulse += dt;
      const s = 1 + Math.sin(this.bossPulse * 2.2) * 0.08;
      this.bossMesh.scale.setScalar(s);
      this.bossMat.emissiveIntensity = 1.8 + Math.sin(this.bossPulse * 3) * 0.6 + this.bossDamageFlash * 1.5;
      // Color shifts white→original as flash decays; boss is bright on hit.
      if (this.bossDamageFlash > 0) {
        this.bossDamageFlash = Math.max(0, this.bossDamageFlash - dt * 4);
      }
      // HP ring shows current/max as arc length.
      if (this.bossHpRing && this.bossHpRingMat) {
        this.bossHpRing.position.copy(this.bossMesh.position);
        const remain = Math.max(0, this.bossHp / this.bossMaxHp);
        // Rebuild geometry for the partial arc — cheap (1 alloc per damage).
        this.bossHpRing.geometry.dispose();
        this.bossHpRing.geometry = new THREE.RingGeometry(
          2.6, 2.9, 64, 1,
          Math.PI / 2,
          remain * Math.PI * 2,
        );
        this.bossHpRingMat.color.setHex(remain > 0.6 ? 0x46ffe0 : remain > 0.3 ? 0xffd54a : 0xff5b3a);
        this.bossHpRingMat.opacity = 0.4 + 0.4 * remain + this.bossDamageFlash * 0.5;
      }
      // Boss dies: fade body + shrink, then clear.
      if (this.bossHp <= 0) {
        this.bossMesh.scale.multiplyScalar(1 - dt * 1.2);
        this.bossMat.emissiveIntensity = Math.max(0, this.bossMat.emissiveIntensity - dt * 4);
        if (this.bossMesh.scale.x < 0.1) this.clearBoss();
      }
    }

    // Trace sample. Normally fires every SAMPLE_INTERVAL_S. If the gap
    // exceeds SAMPLE_INTERVAL_MAX_S (tab throttled, rAF starved), backfill
    // intermediate samples linearly between the last-known position and
    // the current one so the cadence the validator sees stays plausible.
    const gap = runT - this.lastSampleT;
    if (gap >= Hailstorm.SAMPLE_INTERVAL_S) {
      if (this.lastSampleT >= 0 && gap > Hailstorm.SAMPLE_INTERVAL_MAX_S) {
        const steps = Math.floor(gap / Hailstorm.SAMPLE_INTERVAL_S);
        for (let i = 1; i < steps; i++) {
          const tStep = this.lastSampleT + i * Hailstorm.SAMPLE_INTERVAL_S;
          const f = i / steps;
          const x = this.lastSampleX + (this.shipPos.x - this.lastSampleX) * f;
          const y = this.lastSampleY + (this.shipPos.y - this.lastSampleY) * f;
          this.trace.push(
            Math.round(tStep * 100) / 100,
            Math.round(x * 100) / 100,
            Math.round(y * 100) / 100,
          );
        }
      }
      this.trace.push(
        Math.round(runT * 100) / 100,
        Math.round(this.shipPos.x * 100) / 100,
        Math.round(this.shipPos.y * 100) / 100,
      );
      this.lastSampleT = runT;
      this.lastSampleX = this.shipPos.x;
      this.lastSampleY = this.shipPos.y;
    }

    const { dodgesThisFrame, hit, nearestDist } = this.projectiles.update(dt, runT, this.shipPos);

    if (typeof nearestDist === "number" && nearestDist < APPROACH_GATE) {
      const intensity = 1 - nearestDist / APPROACH_GATE;
      this.audio.setApproach(intensity * 0.9);
    } else {
      this.audio.setApproach(0);
    }

    for (const d of dodgesThisFrame as DodgeEvent[]) {
      // v5(c) — record run-time-in-seconds with each dodge so the
      // server can derive time_to_first_dodge_ms + mean/std inter-dodge
      // intervals. Round to 0.01s to match the existing trace
      // precision (10 Hz trace sampler). Per
      // docs/dossier-games/02-hailstorm/07-telemetry.md §3 cadence fields.
      this.dodgeClaims.push({ idx: d.idx, distance: d.distance, t: Math.round(runT * 100) / 100 });
      const detune = d.bucket === 3 ? 12 : d.bucket === 2 ? 7 : 3;
      this.audio.chime(detune, 0);
      if (d.bucket === 3) this.dodgeSpike = 1.0;
    }

    if (this.dodgeSpike > 0) {
      this.dodgeSpike = Math.max(0, this.dodgeSpike - dt * 3.5);
      this.speedLinesMat.opacity = this.dodgeSpike * 0.9;
      this.speedLines.position.set(this.shipPos.x, this.shipPos.y, 0.1);
      this.speedLines.scale.setScalar(1 + (1 - this.dodgeSpike) * 1.8);
    } else {
      this.speedLinesMat.opacity = 0;
    }

    // Boss-kill radial trauma pulse — drives the engine's shared FOV +
    // motion-blur + chromatic + god-rays passes for ~0.6s after a kill.
    // Capped at 0.6 boost so the FOV punch doesn't disorient the player
    // while projectiles are still in flight. Engine no-ops when motion
    // effects are off (reduced-motion users see the existing chime + ring
    // flash, just not the radial pulse).
    if (this.bossKillPulse > 0) {
      this.bossKillPulse = Math.max(0, this.bossKillPulse - dt * 1.6);
      this.engine.setBoost(this.bossKillPulse * 0.6, dt);
    } else if (this.engine.currentBoost > 0.001) {
      this.engine.setBoost(0, dt);
    }

    // Tick down ability cooldowns.
    for (const k of ALL_ABILITIES) {
      if (this.cooldowns[k] > 0) this.cooldowns[k] = Math.max(0, this.cooldowns[k] - dt);
    }

    // Anchors expire visually + materially.
    for (let i = this.anchors.length - 1; i >= 0; i--) {
      const a = this.anchors[i];
      const lifeLeft = a.expiresAt - this.engine.clock.elapsedTime;
      if (lifeLeft <= 0) {
        this.engine.scene.remove(a.mesh);
        a.mesh.geometry.dispose();
        a.haloMat.dispose();
        this.anchors.splice(i, 1);
      } else {
        // Pulse halo while active.
        a.haloMat.opacity = 0.4 + Math.sin(this.engine.clock.elapsedTime * 6) * 0.2;
        a.mesh.scale.setScalar(1 + Math.sin(this.engine.clock.elapsedTime * 4) * 0.05);
      }
    }

    // Phase visual: brighten the ship halo while i-frames active.
    const phaseActive = this.engine.clock.elapsedTime < this.phaseUntil;
    if (phaseActive) {
      this.shipMat.emissiveIntensity = 3.0;
      this.ship.scale.setScalar(1.2);
    } else {
      this.shipMat.emissiveIntensity = 1.2;
      this.ship.scale.setScalar(1);
    }

    // Foresight: ramps projectile mesh emissive so trajectories pop.
    const foresightActive = this.engine.clock.elapsedTime < this.foresightUntil;
    this.projectiles.group.traverse((c) => {
      if (c instanceof THREE.Mesh) {
        const m = c.material as THREE.MeshBasicMaterial;
        if (foresightActive) {
          if (m.opacity !== undefined) m.opacity = 1.0;
          m.color.setHex(0xffd54a);
        } else {
          if (m.opacity !== undefined) m.opacity = 0.95;
        }
      }
    });

    // Determine active visual fx for HUD highlight.
    let activeFx: "phase" | "anchor" | "foresight" | null = null;
    if (phaseActive) activeFx = "phase";
    else if (this.anchors.some((a) => {
      const d = Math.hypot(a.x - this.shipPos.x, a.y - this.shipPos.y);
      return d <= ANCHOR_RADIUS && this.engine.clock.elapsedTime < a.expiresAt;
    })) activeFx = "anchor";
    else if (foresightActive) activeFx = "foresight";

    const dodges = this.projectiles.dodgeCounts.tight + this.projectiles.dodgeCounts.close + this.projectiles.dodgeCounts.clear;
    const wave = WAVE_CYCLE[Math.max(0, this.currentWaveIdx) % WAVE_CYCLE.length];
    this.cb.onHud({
      survivalS: runT,
      dodges,
      waveName: wave.name,
      waveBoss: wave.boss,
      cooldowns: { ...this.cooldowns },
      slowCharges: this.slowCharges,
      activeFx,
    });

    // Hit detection — overridden by isProtected() (phase + anchor i-frames).
    if (hit && !this.isProtected()) {
      this.gameOver = true;
      this.audio.thud();
      this.audio.setDroneIntensity(0);
      this.audio.setApproach(0);
      const baseScore = Math.round(runT * 100) +
        this.projectiles.dodgeCounts.tight * 30 +
        this.projectiles.dodgeCounts.close * 20 +
        this.projectiles.dodgeCounts.clear * 10;
      // Client-side estimate of bonuses (server is authoritative).
      const wavesCleared = Math.floor(runT / WAVE_LEN_S);
      let bossesSurvived = 0;
      for (let i = 0; i < wavesCleared; i++) if (WAVE_CYCLE[i % WAVE_CYCLE.length].boss) bossesSurvived++;
      const score = baseScore
        + wavesCleared * 300
        + bossesSurvived * 1500
        + this.bossKills.length * 3500;
      let prev = 0;
      try { prev = parseInt(localStorage.getItem(PB_KEY) || "0", 10) || 0; } catch { /* private mode */ }
      const isNewBest = score > prev;
      if (isNewBest) {
        try { localStorage.setItem(PB_KEY, String(score)); } catch { /* private mode */ }
      }
      this.trace.push(
        Math.round(runT * 100) / 100,
        Math.round(this.shipPos.x * 100) / 100,
        Math.round(this.shipPos.y * 100) / 100,
      );
      this.cb.onGameOver({
        survivalS: runT,
        score,
        dodges,
        isNewBest,
        dodgeClaims: this.dodgeClaims,
        trace: this.trace,
        abilitiesUsed: this.abilitiesUsed,
        loadout: this.loadout,
        wavesCleared,
        bossesSurvived,
        bossKills: this.bossKills,
      });
    }
  }

  /** v5(a) — in-memory replay of the just-finished run. No server
   *  endpoint, no persistence: the trace + dodge claims are kept in
   *  memory by the caller (main-hailstorm) and passed in here. Re-runs
   *  the projectile schedule against the recorded ship trajectory.
   *
   *  Bosses + abilities are intentionally omitted from the replay —
   *  they require additional event streams the wire payload doesn't
   *  currently carry. v6+ could thread bossKills + abilitiesUsed
   *  through for a fuller playback. */
  playReplay(opts: { trace: number[]; dodgeClaims: { idx: number; distance: number; t?: number }[]; speed?: number }) {
    if (this.replayMode) return;
    if (!opts.trace || opts.trace.length < 3) return;
    this.replayMode = true;
    this.replayTrace = opts.trace;
    this.replayDodgeIdx = new Set(opts.dodgeClaims.map((d) => d.idx));
    this.replayT = 0;
    this.replayEndT = opts.trace[opts.trace.length - 3]; // last triplet's t
    this.replaySpeed = opts.speed ?? 1;
    this.clearBoss();
    // Clear any anchors left over from the live run so the replay scene
    // starts clean. Anchors aren't part of the trace — they were placed
    // by ability use, which the replay doesn't reproduce.
    for (const a of this.anchors) {
      this.engine.scene.remove(a.mesh);
      a.mesh.geometry.dispose();
      a.haloMat.dispose();
    }
    this.anchors = [];
    this.projectiles.reset(this.seed);
    // Initial ship position = first trace sample.
    this.shipPos.x = opts.trace[1];
    this.shipPos.y = opts.trace[2];
    this.ship.position.set(this.shipPos.x, this.shipPos.y, 0);
    this.audio.setDroneIntensity(0.3);
    this.audio.setApproach(0);
  }

  stopReplay() {
    if (!this.replayMode) return;
    this.replayMode = false;
    this.projectiles.reset(this.seed);
    this.audio.setApproach(0);
    this.audio.setDroneIntensity(0);
    if (this.replayDoneCb) { const cb = this.replayDoneCb; this.replayDoneCb = null; cb(); }
  }

  onReplayDone(cb: () => void) { this.replayDoneCb = cb; }

  /** Linear-interp ship position at replay time `t` (seconds) from the
   *  10 Hz trace. Returns null past the end of the trace. */
  private traceLookup(t: number): { x: number; y: number } | null {
    const trace = this.replayTrace;
    if (trace.length < 6) return { x: trace[1], y: trace[2] };
    if (t >= this.replayEndT) return null;
    // Binary search for the bracketing samples. Each sample is 3 numbers
    // (t, x, y) so stride is 3.
    let lo = 0;
    let hi = (trace.length / 3) - 1;
    while (lo + 1 < hi) {
      const mid = (lo + hi) >> 1;
      if (trace[mid * 3] <= t) lo = mid; else hi = mid;
    }
    const t0 = trace[lo * 3];
    const t1 = trace[hi * 3];
    const span = t1 - t0;
    const f = span > 0 ? Math.max(0, Math.min(1, (t - t0) / span)) : 0;
    return {
      x: trace[lo * 3 + 1] + (trace[hi * 3 + 1] - trace[lo * 3 + 1]) * f,
      y: trace[lo * 3 + 2] + (trace[hi * 3 + 2] - trace[lo * 3 + 2]) * f,
    };
  }

  private replayFrame(dt: number) {
    this.replayT += dt * this.replaySpeed;
    const pos = this.traceLookup(this.replayT);
    if (pos === null) {
      // Replay finished naturally.
      this.stopReplay();
      return;
    }
    this.shipPos.x = pos.x;
    this.shipPos.y = pos.y;
    this.ship.position.set(this.shipPos.x, this.shipPos.y, 0);
    // Faint visual marker so the player knows it's a replay, not live.
    this.shipMat.emissiveIntensity = 0.6 + Math.sin(this.replayT * 4) * 0.2;

    const stepT = this.replayT;
    const { dodgesThisFrame, nearestDist } = this.projectiles.update(dt * this.replaySpeed, stepT, this.shipPos);

    if (typeof nearestDist === "number" && nearestDist < APPROACH_GATE) {
      this.audio.setApproach((1 - nearestDist / APPROACH_GATE) * 0.6);
    } else {
      this.audio.setApproach(0);
    }
    for (const d of dodgesThisFrame as DodgeEvent[]) {
      if (this.replayDodgeIdx.has(d.idx)) {
        const detune = d.bucket === 3 ? 12 : d.bucket === 2 ? 7 : 3;
        this.audio.chime(detune, 0);
        if (d.bucket === 3) this.dodgeSpike = 1.0;
      }
    }
    if (this.dodgeSpike > 0) {
      this.dodgeSpike = Math.max(0, this.dodgeSpike - dt * 3.5);
      this.speedLinesMat.opacity = this.dodgeSpike * 0.9;
      this.speedLines.position.set(this.shipPos.x, this.shipPos.y, 0.1);
      this.speedLines.scale.setScalar(1 + (1 - this.dodgeSpike) * 1.8);
    } else {
      this.speedLinesMat.opacity = 0;
    }
  }

  dispose() {
    this.canvas.removeEventListener("pointerdown", this.onPointerDown);
    this.canvas.removeEventListener("pointermove", this.onPointerMove);
    window.removeEventListener("pointerup", this.onPointerUp);
    window.removeEventListener("keydown", this.onKeyDown);
    for (const a of this.anchors) {
      this.engine.scene.remove(a.mesh);
      a.mesh.geometry.dispose();
      a.haloMat.dispose();
    }
    this.anchors = [];
    this.clearBoss();
    this.engine.dispose();
  }
}

export { ABILITY_LABEL, ALL_ABILITIES, LOADOUT_KEY };
